import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import type { PreviewRow } from './types';

interface AllocationPreviewTableProps {
  rows: PreviewRow[];
}

export const DeltaCell = ({ value }: { value: number }) => {
  const theme = useTheme();
  if (value === 0) {
    return <span>0</span>;
  }
  const positive = value > 0;
  return (
    <Box
      component="span"
      sx={{
        fontWeight: 600,
        color: positive ? theme.palette.success.main : theme.palette.error.main,
      }}
    >
      {positive ? `+${value}` : value}
    </Box>
  );
};

export const AllocationPreviewTable = ({ rows }: AllocationPreviewTableProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Table size="medium">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.grey[100],
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <TableCell>
              {formatMessage({ id: 'adminProjects.reallocation.preview.annotator' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.pendingBefore' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.deltaPending' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.pendingAfter' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.draft' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.finalized' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.totalBefore' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.totalAfter' })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const changed = row.deltaPending !== 0;
            return (
              <TableRow
                key={row.annotatorId}
                sx={{
                  backgroundColor: changed ? theme.palette.action.hover : 'inherit',
                  '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    {row.isAiAgent && (
                      <SmartToyOutlinedIcon
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                    )}
                    {row.annotatorName}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {row.pendingBefore.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <DeltaCell value={row.deltaPending} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {row.pendingAfter.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                  {row.draftCount.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                  {row.finalisedCount.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {row.totalBefore.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {row.totalAfter.toLocaleString()}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
